import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useEffect, useMemo } from "react";
import { ModalLoading } from "../../../../../../common/components/modal_loading";

import PrimaryButton from "../../../../../../common/components/primary_button";
import FormContainer from "../../../../../../common/components/form_container";
import DefaultFormInput from "../../../../../../common/components/default_form_input";

import { FormRadioGroupInput } from "../../../../../../common/components/masked_inputs/react_forms/form_radio_group_input";
import {
  DiarioObrasEfetivoForm,
  DiarioObrasEfetivoSchema,
} from "../../../schemas/DiarioObrasEfetivo/diario_obras_efetivo";
import { DropDownCargo } from "../../../../../../common/components/dropdown_cargo";
import { DropDownFornecedor } from "../../../../../../common/components/dropdown_fornecedor";
import { LogTipoTabela } from "../../../../../../common/utils/log_tabela_tipo";
import { isNotNullOrUndefined } from "../../../../../../common/utils/utils";
import { DefaultMultiAutocomplete } from "../../../../../../common/components/default_multi_autocomplete";
import { FuncionarioDropDown } from "../../../../../../common/domain/models/funcionario_dropdown";
import { FuncionarioTercDropDown } from "../../../../../../common/domain/models/funcionario_terc_dropdown";

type Props = {
  open: boolean;
  saving: boolean;
  selectedEfetivo: DiarioObrasEfetivoForm | null;
  funcionarioDropdown: FuncionarioDropDown[] | [];
  funcionarioTerceirizadoDropdown: FuncionarioTercDropDown[] | [];
  IdDiarioObras: number;
  onSubmit: (form: DiarioObrasEfetivoForm) => void;
  onOpenChangeHistory: (id: number, tabelaTipo: LogTipoTabela) => void;
  loadFuncionarioDropDown: (idCargo: number) => void;
  loadFuncionarioTerceirizadoDropDown: (idFornecedor: number) => void;
  onClose: () => void;
};

export const EfetivoModal = ({
  open,
  saving,
  IdDiarioObras,
  selectedEfetivo,
  funcionarioDropdown,
  funcionarioTerceirizadoDropdown,
  loadFuncionarioTerceirizadoDropDown,
  onOpenChangeHistory,
  loadFuncionarioDropDown,
  onSubmit,
  onClose,
}: Props) => {
  const hookForm = useForm<DiarioObrasEfetivoForm>({
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    resolver: zodResolver(DiarioObrasEfetivoSchema),
  });

  useEffect(() => {
    if (open) {
      if (selectedEfetivo) {
        hookForm.reset({
          IdDiarioObrasEfetivo: selectedEfetivo.IdDiarioObrasEfetivo,
          IdDiarioObras: selectedEfetivo.IdDiarioObras,
          Descricao: selectedEfetivo.Descricao,
          IdCargo: selectedEfetivo.IdCargo,
          Categoria: selectedEfetivo.Categoria,
          IdFornecedor: selectedEfetivo.IdFornecedor,
          Quantidade: selectedEfetivo.Quantidade,
          Tipo: selectedEfetivo.Tipo,
          IdsFuncionarios: selectedEfetivo.IdsFuncionarios,
        });
      }
    } else {
      hookForm.reset({
        IdDiarioObrasEfetivo: null,
        IdDiarioObras: IdDiarioObras,
        Descricao: "",
        IdCargo: 0,
        Categoria: 0,
        IdFornecedor: null,
        Quantidade: null,
        Tipo: 0,
        IdsFuncionarios: [],
      });
    }
  }, [hookForm, open, selectedEfetivo, IdDiarioObras]);

  const handleChangeHistory = useCallback(() => {
    if (selectedEfetivo) {
      onOpenChangeHistory(
        Number(selectedEfetivo.IdDiarioObrasEfetivo),
        LogTipoTabela.DIARIO_OBRAS_EFETIVO,
      );
    }
  }, [selectedEfetivo, onOpenChangeHistory]);

  const handleSubmit = useCallback(
    (form: DiarioObrasEfetivoForm) => {
      if (!saving) {
        onSubmit(form);
      }
    },
    [onSubmit, saving],
  );

  const handleClose = useCallback(() => {
    if (!saving) {
      onClose();
      hookForm.reset();
    }
  }, [hookForm, onClose, saving]);


  const tipoMaoDeObraValue = hookForm.watch("Tipo");
  const idCargoValue = hookForm.watch("IdCargo");
  const idFornecedorValue = hookForm.watch("IdFornecedor");
  const idsFuncionariosValue = hookForm.watch("IdsFuncionarios") || [];


  useEffect(() => {
    if (selectedEfetivo && !hookForm.formState.isDirty) {
      return;
    }

    if (Number(tipoMaoDeObraValue) === 0) {
      hookForm.setValue("IdFornecedor", null);
      hookForm.setValue("IdsFuncionarios", []);
    } else if (Number(tipoMaoDeObraValue) === 1) {
      hookForm.setValue("IdsFuncionarios", []);
    }
  }, [hookForm, tipoMaoDeObraValue, selectedEfetivo]);

  useEffect(() => {
    if (
      Number(tipoMaoDeObraValue) === 0 &&
      idCargoValue &&
      Number(idCargoValue) > 0
    ) {
      loadFuncionarioDropDown(Number(idCargoValue));
    }
  }, [idCargoValue, tipoMaoDeObraValue, loadFuncionarioDropDown]);

  useEffect(() => {
    if (
      Number(tipoMaoDeObraValue) === 1 &&
      idFornecedorValue &&
      Number(idFornecedorValue) > 0
    ) {
      loadFuncionarioTerceirizadoDropDown(Number(idFornecedorValue));
    }
  }, [
    idFornecedorValue,
    tipoMaoDeObraValue,
    loadFuncionarioTerceirizadoDropDown,
  ]);

  return (
    <Dialog
      open={open}
      fullWidth={true}
      maxWidth="md"
      slotProps={{
        paper: {
          component: "form",
          onSubmit: hookForm.handleSubmit(handleSubmit),
        },
      }}
      sx={{
        "& .MuiDialog-container": {
          "& .MuiPaper-root": {
            width: "100%",
            maxWidth: "42rem",
          },
        },
      }}
    >
      <DialogTitle>Efetivo</DialogTitle>

      <DialogContent>
        <ModalLoading loading={saving}>
          <FormContainer
            title={selectedEfetivo ? "Editar" : "Cadastrar"}
            arrangement="fullRow"
          >
            <>
              <DropDownCargo
                name="IdCargo"
                required={true}
                reactHookForm={hookForm}
              />
              <div className="md:col-span-2">
                <DefaultFormInput
                  name="Descricao"
                  label="Descrição"
                  multiline={true}
                  rows={4}
                  reactHookForm={hookForm}
                />
              </div>

              <FormRadioGroupInput
                name="Tipo"
                label="Tipo de mão de Obra"
                readOnly={saving}
                options={[
                  { id: 0, label: "Própria" },
                  { id: 1, label: "Terceirizada" },
                ]}
                reactHookForm={hookForm}
              />

              {/* Renderiza campo de Fornecedor APENAS se for Terceirizada (1) */}
              {Number(tipoMaoDeObraValue) === 1 && (
                <div className="grid-cols-3 gap-4 w-full justify-between">
                  <DropDownFornecedor
                    name="IdFornecedor"
                    label="Fornecedor:"
                    required={true}
                    readOnly={saving}
                    reactHookForm={hookForm}
                  />
                </div>
              )}

              <FormRadioGroupInput
                name="Categoria"
                label="Categoria de mão de Obra"
                readOnly={saving}
                options={[
                  { id: 0, label: "Administrativa" },
                  { id: 1, label: "Operacional" },
                ]}
                reactHookForm={hookForm}
              />

              <DefaultFormInput
                name="Quantidade"
                label="Quantidade"
                reactHookForm={hookForm}
              />

              {Number(tipoMaoDeObraValue) === 0 && Number(idCargoValue) > 0 && (
                <DefaultMultiAutocomplete
                  label="Funcionarios"
                  options={funcionarioDropdown}
                  getOptionKey={(x) => x.idFuncionario}
                  getOptionLabel={(x) => x.nome}
                  value={funcionarioDropdown.filter((f) =>
                    idsFuncionariosValue.includes(f.idFuncionario),
                  )}
                  onChange={(itensSelecionados) => {
                    if (!itensSelecionados) {
                      hookForm.setValue("IdsFuncionarios", []);
                      return;
                    }
                    const ids = itensSelecionados.map(
                      (item: any) => item.idFuncionario,
                    );
                    hookForm.setValue("IdsFuncionarios", ids, {
                      shouldDirty: true,
                      shouldValidate: true,
                    });
                  }}
                />
              )}

              {Number(tipoMaoDeObraValue) === 1 &&
                Number(idFornecedorValue) > 0 && (
                  <DefaultMultiAutocomplete
                    label="Funcionarios Tercerizados"
                    options={funcionarioTerceirizadoDropdown}
                    getOptionKey={(x) => x.idFuncionarioTerc}
                    getOptionLabel={(x) => x.nome}
                    value={funcionarioTerceirizadoDropdown.filter((f) =>
                      idsFuncionariosValue.includes(f.idFuncionarioTerc),
                    )}
                    onChange={(itensSelecionados) => {
                      if (!itensSelecionados) {
                        hookForm.setValue("IdsFuncionarios", []);
                        return;
                      }
                      const ids = itensSelecionados.map(
                        (item: any) => item.idFuncionarioTerc,
                      );
                      hookForm.setValue("IdsFuncionarios", ids, {
                        shouldDirty: true,
                        shouldValidate: true,
                      });
                    }}
                  />
                )}
            </>
          </FormContainer>
        </ModalLoading>
      </DialogContent>

      <DialogActions>
        <div className="w-full h-full items-center justify-between flex p-4 gap-4">
          <div className="w-[220px]">
            {isNotNullOrUndefined(selectedEfetivo) && (
              <PrimaryButton buttonColor="yellow" onClick={handleChangeHistory}>
                <span className="text-xs">Historico de alterações</span>
              </PrimaryButton>
            )}
          </div>
          <div className="flex flex-row gap-2 w-full justify-end">
            <div className="p-2 hover:bg-hover-light-gray cursor-pointer dark:hover:bg-border-dark-gray rounded-[4px] font-semibold">
              <h1
                className="text-sm text-black dark:text-white"
                onClick={handleClose}
              >
                Cancelar
              </h1>
            </div>

            <div className="w-[120px]">
              {saving ? (
                <PrimaryButton buttonColor="primary-green">
                  Salvando...
                </PrimaryButton>
              ) : (
                <PrimaryButton type="submit" buttonColor="primary-green">
                  Salvar
                </PrimaryButton>
              )}
            </div>
          </div>
        </div>
      </DialogActions>
    </Dialog>
  );
};
